import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import AppButton from "../components/buttons/AppButton";

import GenerateRenewalModal from "../components/invoices/GenerateRenewalModal";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getInvoices } from "../redux/actions/invoiceActions";

const InvoicesPage = () => {
  const dispatch = useDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .split("T")[0];

  const { invoices, pagination, loadingInvoices, errorLoadingInvoices } =
    useSelector((state) => state.invoice);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);

  const canRead = hasPermission("invoice:read_all");
  const canCreate = hasPermission("invoice:create");

  const fetchInvoicesList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getInvoices({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchInvoicesList();
  }, [fetchInvoicesList]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const formatPlainNumber = (val) =>
    Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const columns = useMemo(
    () => [
      {
        id: "invoiceNumber",
        label: "Invoice #",
        minWidth: 120,
        format: (val) => <b style={{ color: "#c77dff" }}>{val}</b>,
      },
      {
        id: "issueDate",
        label: "Issue Date",
        minWidth: 100,
        format: (val) => new Date(val).toLocaleDateString(),
      },
      { id: "customerBusinessName", label: "Customer", minWidth: 160 },
      {
        id: "status",
        label: "Status",
        minWidth: 110,
        format: (val) => <StatusChip status={val} />,
      },
      {
        id: "total",
        label: "Total",
        minWidth: 110,
        align: "right",
        format: (val) => (
          <Typography variant="body2" fontWeight="bold">
            {formatPlainNumber(val)}
          </Typography>
        ),
      },
      {
        id: "amountPaid",
        label: "Paid",
        minWidth: 110,
        align: "right",
        format: (val) => (
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {formatPlainNumber(val)}
          </Typography>
        ),
      },
      {
        id: "balance",
        label: "Balance",
        minWidth: 110,
        align: "right",
        format: (_, row) => {
          const due = (row.total || 0) - (row.amountPaid || 0);
          return (
            <Typography
              variant="body2"
              fontWeight="bold"
              color={due > 0.01 ? "error.main" : "text.secondary"}
            >
              {formatPlainNumber(due)}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Invoices"
      subtitle="Manage customer billing, track payments, and follow up on licensing accounts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AutorenewIcon />}
            onClick={() => setIsRenewalModalOpen(true)}
          >
            Generate Renewal
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search Invoice # or Customer..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={invoices}
        loading={loadingInvoices}
        error={errorLoadingInvoices}
        onRetry={fetchInvoicesList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={false}
        rowAction={(row) => (
          <Tooltip title="View Invoice">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigateToTab(`/invoices/${row.id}`)}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      />

      {isRenewalModalOpen && (
        <GenerateRenewalModal
          open={isRenewalModalOpen}
          onClose={() => {
            setIsRenewalModalOpen(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default InvoicesPage;
